import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import usePersistentState from '../utils/usePersistentState';
import toast from 'react-hot-toast';
import { clientAPI, authAPI, requirementAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button, Input, Select, Textarea, Modal, Badge, EmptyState, Pagination, Spinner, Card, ConfirmModal} from '../components/UI';
import RichTextEditor from '../components/RichTextEditor';

const emptyForm = {
  clientName: '', companyDetails: '', contactEmail: '',
  contactPhone: '', industry: '', website: '',
  // No reportScheduler* keys: the Add/Edit modal doesn't show them, and echoing
  // the list row's copy back on Update would overwrite a schedule saved since
  // (it's edited only in the Report Schedule modal).
};

/* ── Multi-email tag input ───────────────────────────────────────────────── */
function EmailTagInput({ value = '', onChange, placeholder, isDark }) {
  // value is a comma-separated string stored in DB
  const tags    = value ? value.split(',').map(e => e.trim()).filter(Boolean) : [];
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  const addTag = (raw) => {
    const email = raw.trim().toLowerCase();
    if (!email) return;
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isValid) return;
    if (tags.includes(email)) { setInput(''); return; }
    onChange([...tags, email].join(','));
    setInput('');
  };

  const removeTag = (idx) => {
    const next = tags.filter((_, i) => i !== idx);
    onChange(next.join(','));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(input); }
    if (e.key === 'Backspace' && !input && tags.length) removeTag(tags.length - 1);
  };

  const base = { fontSize: 13, background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#c7d2fe'}`, color: isDark ? '#f1f5f9' : '#111827' };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      style={{ ...base, borderRadius: 8, padding: '6px 10px', display: 'flex', flexWrap: 'wrap', gap: 5, cursor: 'text', minHeight: 40, alignItems: 'center' }}
    >
      {tags.map((tag, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px 2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff', color: isDark ? '#a5b4fc' : '#4338ca', border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}` }}>
          {tag}
          <span onClick={e => { e.stopPropagation(); removeTag(i); }} style={{ cursor: 'pointer', fontSize: 13, lineHeight: 1, color: isDark ? '#818cf8' : '#6366f1', marginLeft: 1 }}>×</span>
        </span>
      ))}
      <input
        ref={inputRef}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => addTag(input)}
        placeholder={tags.length === 0 ? (placeholder || 'Type email and press Enter…') : ''}
        style={{ flex: 1, minWidth: 100, border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: isDark ? '#f1f5f9' : '#111827' }}
      />
    </div>
  );
}

/* ── Requirement multi-select ────────────────────────────────────────────── */
function ReqMultiSelect({ selected, onChange, options, isDark }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };

  const selectedLabels = options.filter(o => selected.includes(o.id));

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ minHeight: 38, padding: '5px 10px', borderRadius: 8, cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', background: isDark ? 'rgba(255,255,255,0.07)' : '#f8fafc', border: `1.5px solid ${open ? '#6366f1' : (isDark ? 'rgba(255,255,255,0.15)' : '#c7d2fe')}` }}
      >
        {selectedLabels.length === 0
          ? <span style={{ fontSize: 12, color: isDark ? '#64748b' : '#94a3b8' }}>Select requirements…</span>
          : selectedLabels.map(o => (
              <span key={o.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: isDark ? 'rgba(99,102,241,0.25)' : '#eef2ff', color: isDark ? '#a5b4fc' : '#4338ca', border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}` }}>
                {o.jobTitle}
                <span onClick={e => { e.stopPropagation(); toggle(o.id); }} style={{ cursor: 'pointer', opacity: 0.7, fontSize: 12, lineHeight: 1 }}>×</span>
              </span>
            ))
        }
        <span style={{ marginLeft: 'auto', fontSize: 11, color: isDark ? '#6366f1' : '#818cf8' }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 200, background: isDark ? '#1e293b' : '#fff', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 200, overflowY: 'auto' }}>
          {options.length === 0
            ? <div style={{ padding: '12px 14px', fontSize: 12, color: isDark ? '#64748b' : '#94a3b8' }}>No open/on-hold requirements found</div>
            : options.map(o => {
                const checked = selected.includes(o.id);
                return (
                  <div key={o.id} onClick={() => toggle(o.id)} style={{ padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, background: checked ? (isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff') : 'transparent', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}` }}>
                    <div style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, border: `2px solid ${checked ? '#6366f1' : (isDark ? '#475569' : '#cbd5e1')}`, background: checked ? '#6366f1' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {checked && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                    </div>
                    <span style={{ fontSize: 13, color: isDark ? '#f1f5f9' : '#0f172a' }}>{o.jobTitle}</span>
                    <span style={{ fontSize: 11, marginLeft: 'auto', padding: '1px 7px', borderRadius: 99, background: o.status === 'Open' ? '#ecfdf5' : '#fffbeb', color: o.status === 'Open' ? '#065f46' : '#92400e', border: `1px solid ${o.status === 'Open' ? '#6ee7b7' : '#fcd34d'}` }}>{o.status}</span>
                  </div>
                );
              })
          }
        </div>
      )}
    </div>
  );
}

/* ── Scheduler section component ─────────────────────────────────────────── */
function SchedulerSection({ form, setForm, isDark, clientRequirements = [] }) {
  const selMode = form._reqMode || ((!Array.isArray(form.reportSchedulerRequirements) || form.reportSchedulerRequirements.length === 0) ? 'all' : 'select');
  const selAll = selMode === 'all';

  return (
    <div style={{ border: `1.5px solid ${isDark ? 'rgba(99,102,241,0.25)' : '#e0e7ff'}`, borderRadius: 12 }}>
      <div style={{ padding: '11px 16px', background: isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff', borderBottom: form.reportSchedulerEnabled ? `1px solid ${isDark ? 'rgba(99,102,241,0.15)' : '#e0e7ff'}` : 'none', borderRadius: form.reportSchedulerEnabled ? '11px 11px 0 0' : 11, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15 }}>📊</span>
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#a5b4fc' : '#4338ca', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Daily Report Scheduler</span>
            <p style={{ margin: '1px 0 0', fontSize: 11, color: isDark ? '#6366f1' : '#6366f1' }}>Send a 24-hour activity report to requirement contacts</p>
          </div>
        </div>
        <div
          onClick={() => setForm(f => ({ ...f, reportSchedulerEnabled: !f.reportSchedulerEnabled }))}
          style={{ width: 44, height: 24, borderRadius: 99, cursor: 'pointer', flexShrink: 0, transition: 'background .2s', display: 'flex', alignItems: 'center', padding: '0 3px', background: form.reportSchedulerEnabled ? '#6366f1' : (isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db') }}
        >
          <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.2)', transition: 'transform .2s', transform: form.reportSchedulerEnabled ? 'translateX(20px)' : 'translateX(0)' }} />
        </div>
      </div>

      {form.reportSchedulerEnabled && (
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Time + Window */}
          <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr]" style={{ gap: 12, alignItems: 'start' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: isDark ? '#94a3b8' : '#4b5563', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Send Time (24-hr)</label>
              <input
                type="time"
                value={form.reportSchedulerTime || '09:00'}
                onChange={e => setForm(f => ({ ...f, reportSchedulerTime: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13, background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#c7d2fe'}`, color: isDark ? '#f1f5f9' : '#111827', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: isDark ? '#94a3b8' : '#4b5563', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Report Window</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[{ value: '24h', label: '⏱ Last 24 Hours' }, { value: 'all', label: '📋 All Time' }].map(opt => {
                  const active = (form.reportSchedulerWindow || '24h') === opt.value;
                  return (
                    <Button key={opt.value} type="button" size="sm"
                      variant={active ? 'primary' : 'ghost'}
                      onClick={() => setForm(f => ({ ...f, reportSchedulerWindow: opt.value }))}
                      style={{ flex: 1 }}>
                      {opt.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* CC Emails — full width */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: isDark ? '#94a3b8' : '#4b5563', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              CC Emails <span style={{ fontWeight: 400, textTransform: 'none', color: isDark ? '#6366f1' : '#818cf8' }}>(added to all reports as CC)</span>
            </label>
            <EmailTagInput
              value={form.reportSchedulerEmail || ''}
              onChange={val => setForm(f => ({ ...f, reportSchedulerEmail: val }))}
              placeholder="Type email & press Enter to add…"
              isDark={isDark}
            />
          </div>

          {/* Requirements filter */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: isDark ? '#94a3b8' : '#4b5563', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Requirements</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {[{ value: 'all', label: '📋 All Requirements' }, { value: 'select', label: '🎯 Select Requirements' }].map(opt => {
                const active = selMode === opt.value;
                return (
                  <Button key={opt.value} type="button" size="sm"
                    variant={active ? 'primary' : 'ghost'}
                    onClick={() => setForm(f => ({ ...f, _reqMode: opt.value, reportSchedulerRequirements: opt.value === 'all' ? [] : f.reportSchedulerRequirements }))}
                    style={{ flex: 1 }}>
                    {opt.label}
                  </Button>
                );
              })}
            </div>
            {!selAll && (
              <ReqMultiSelect
                selected={form.reportSchedulerRequirements || []}
                onChange={ids => setForm(f => ({ ...f, reportSchedulerRequirements: ids }))}
                options={clientRequirements}
                isDark={isDark}
              />
            )}
          </div>

          <p style={{ margin: 0, fontSize: 11, color: '#6366f1', background: isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff', padding: '8px 10px', borderRadius: 7 }}>
            📬 Reports are sent per requirement to that requirement's contact persons. The client contact email is always CC'd. Additional CC emails above are included on every report.
          </p>
        </div>
      )}
    </div>
  );
}

export default function Clients() {
  const { isSuperAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [clients, setClients]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [pages, setPages]           = useState(1);
  const [page, setPage]             = useState(1);
  // Kept for this tab — survives leaving the page and coming back.
  const [search, setSearch]         = usePersistentState('clients:search', '');
  const [loading, setLoading]       = useState(true);
  const [modalOpen, setModalOpen]   = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [form, setForm]             = useState(emptyForm);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(null);
  const [assigning, setAssigning]   = useState(false);
  const [managers, setManagers]     = useState([]);
  const [selectedManager, setSelectedManager] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await clientAPI.getAll({ page, limit: 10, search });
      setClients(data.clients);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [page, search]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isSuperAdmin()) {
      authAPI.getUsers({ role: 'RecruiterManager', limit: 100 })
        .then(({ data }) => setManagers(data.users))
        .catch(() => {});
    }
  }, [isSuperAdmin]);

  const openCreate = () => { setEditClient(null); setForm(emptyForm); setModalOpen(true); };

  const openEdit = (client) => {
    setEditClient(client);
    setForm({
      clientName:              client.clientName,
      companyDetails:          client.companyDetails || '',
      contactEmail:            client.contactEmail,
      contactPhone:            client.contactPhone || '',
      industry:                client.industry || '',
      website:                 client.website || '',
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editClient) {
        await clientAPI.update(editClient.id, form);
        toast.success('Client updated!');
        // Optimistic update
        setClients(prev => prev.map(c => c.id === editClient.id ? { ...c, ...form } : c));
      } else {
        await clientAPI.create(form);
        toast.success('Client created!');
        load(); // Re-fetch for new client (need server-generated ID)
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleDelete = (id) => { setConfirmDelete(id); };

  const handleDeleteConfirmed = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    setDeleting(id);
    try {
      await clientAPI.delete(id);
      toast.success('Client deleted');
      load();
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  const [sendingReport, setSendingReport] = useState(false);
  const handleSendReportNow = async (clientId) => {
    setSendingReport(clientId);
    try {
      await clientAPI.sendReport(clientId);
      toast.success('Report sent successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  // Send Now from inside the schedule modal — saves current form first, then sends
  const handleSendNowFromModal = async () => {
    if (!scheduleClient) return;
    setSendingReport(scheduleClient.id);
    try {
      // Strip UI-only field before saving
      const { _reqMode, ...payload } = scheduleForm;
      const { data } = await clientAPI.update(scheduleClient.id, payload);
      // Reflect the saved schedule in the list right away (the report send
      // below may still fail) so reopening either modal shows it, not the
      // stale row.
      const saved = data?.client ? { ...payload, ...data.client } : payload;
      setClients(prev => prev.map(c => c.id === scheduleClient.id ? { ...c, ...saved } : c));
      setScheduleClient(sc => (sc && sc.id === scheduleClient.id ? { ...sc, ...saved } : sc));
      await clientAPI.sendReport(scheduleClient.id);
      toast.success('Schedule saved & report sent!');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  // ── Schedule modal ───────────────────────────────────────────────────────
  const [scheduleModal, setScheduleModal]   = useState(false);
  const [scheduleClient, setScheduleClient] = useState(null);
  const [scheduleForm, setScheduleForm]     = useState({ reportSchedulerEnabled: false, reportSchedulerTime: '09:00', reportSchedulerEmail: '', reportSchedulerWindow: '24h', reportSchedulerRequirements: [] });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [clientReqs, setClientReqs]         = useState([]);

  const openSchedule = async (client) => {
    setScheduleClient(client);
    setScheduleForm({
      reportSchedulerEnabled:      client.reportSchedulerEnabled      || false,
      reportSchedulerTime:         client.reportSchedulerTime         || '09:00',
      reportSchedulerEmail:        client.reportSchedulerEmail        || '',
      reportSchedulerWindow:       client.reportSchedulerWindow       || '24h',
      reportSchedulerRequirements: client.reportSchedulerRequirements || [],
      _reqMode: (client.reportSchedulerRequirements?.length > 0) ? 'select' : 'all',
    });
    setScheduleModal(true);
    // Fetch open/on-hold requirements for this client (filter status client-side)
    try {
      const res = await requirementAPI.getAll({ clientId: client.id, limit: 200 });
      const all = res.data?.requirements || res.data?.data || [];
      setClientReqs(all.filter(r => r.status === 'Open' || r.status === 'On Hold'));
    } catch { setClientReqs([]); }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const { _reqMode, ...payload } = scheduleForm;
      await clientAPI.update(scheduleClient.id, payload);
      toast.success('Schedule saved!');
      setScheduleModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleAssignManager = async () => {
    if (!selectedManager) return;
    setAssigning(true);
    try {
      await clientAPI.assignManager(selectedClient.id, { managerId: selectedManager });
      toast.success('Manager assigned!');
      setAssignModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign');
    } finally {
      setAssigning(false);
    }
  };

  // ── Scroll-lock: prevent body scroll when any overlay is open ────────────
  const anyOverlayOpen = modalOpen || scheduleModal || assignModal || !!confirmDelete;
  useLayoutEffect(() => {
    if (anyOverlayOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [anyOverlayOpen]);
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Clients</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{total} total clients</p>
        </div>
        {isSuperAdmin() && <Button onClick={openCreate}>+ Add Client</Button>}
      </div>

      <div className="flex gap-3">
        <Input placeholder="Search clients..." value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} className="max-w-sm" />
      </div>

      <Card>
        {loading ? <div className="py-16 flex justify-center"><Spinner /></div> : clients.length === 0 ? (
          <EmptyState icon="🏢" title="No clients found" message="Add your first client."
            action={isSuperAdmin() && <Button onClick={openCreate}>Add Client</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  {['Client', 'Industry', 'Contact', 'Manager', 'Actions'].map(h => (
                    <th key={h} style={{
                      textAlign: 'left', fontSize: 11, fontWeight: 600,
                      color: 'var(--text-muted)', textTransform: 'uppercase',
                      letterSpacing: '0.06em', paddingBottom: 12, paddingRight: 16,
                      whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clients.map((client, idx) => (
                  <tr
                    key={client.id}
                    style={{
                      borderBottom: idx < clients.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '14px 16px 14px 0' }}>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{client.clientName}</p>
                        {client.website && (
                          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, maxWidth: '100%', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
                            {client.website}
                          </p>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px 14px 0' }}>
                      {client.industry
                        ? <Badge variant="info">{client.industry}</Badge>
                        : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>N/A</span>}
                    </td>
                    <td style={{ padding: '14px 16px 14px 0' }}>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{client.contactEmail}</p>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{client.contactPhone}</p>
                    </td>
                    <td style={{ padding: '14px 16px 14px 0' }}>
                      {client.recruiterManager ? (
                        <div>
                          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{client.recruiterManager.name}</p>
                          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{client.recruiterManager.email}</p>
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>Unassigned</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 0' }}>
                      {isSuperAdmin() && (
                        <div className="flex gap-2 flex-wrap">
                          <Button variant="outline" size="xs" onClick={() => openEdit(client)}>✏️ Edit</Button>
                          <Button variant="success" size="xs" onClick={() => openSchedule(client)}>
                            📊 Schedule
                            {client.reportSchedulerEnabled && (
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                            )}
                          </Button>
                          <Button variant="primary" size="xs"
                            onClick={() => {
                              setSelectedClient(client);
                              setSelectedManager(client.recruiterManager?.id || '');
                              setAssignModal(true);
                            }}
                          >👤 Assign</Button>
                          <div onClick={(e) => e.stopPropagation()}><Button variant="danger" size="xs" loading={deleting === client.id} loadingText="Deleting…" onClick={() => handleDelete(client.id)}>Delete</Button></div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Pagination page={page} pages={pages} onPage={setPage} />

      {/* Create / Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editClient ? 'Edit Client' : 'Add New Client'}>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Client Name *" value={form.clientName}
              onChange={e => setForm({ ...form, clientName: e.target.value })} required className="col-span-2" />
            <Input label="Contact Email *" type="email" value={form.contactEmail}
              onChange={e => setForm({ ...form, contactEmail: e.target.value })} required />
            <Input label="Contact Phone" value={form.contactPhone}
              onChange={e => setForm({ ...form, contactPhone: e.target.value })} />
            <Input label="Industry" value={form.industry}
              onChange={e => setForm({ ...form, industry: e.target.value })} />
            <Input label="Website" value={form.website}
              onChange={e => setForm({ ...form, website: e.target.value })} />
          </div>
          <RichTextEditor label="Company Details" value={form.companyDetails} rows={4}
            onChange={(html) => setForm({ ...form, companyDetails: html })} />

          <div className="flex gap-3 justify-end">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>{editClient ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      {/* ── Schedule Modal ── */}
      <Modal isOpen={scheduleModal} onClose={() => setScheduleModal(false)}
        title={`📊 Report Schedule — ${scheduleClient?.clientName}`}>
        <div className="space-y-4">
          <SchedulerSection form={scheduleForm} setForm={setScheduleForm} isDark={isDark} clientRequirements={clientReqs} />
          <div className="flex gap-3 justify-end" style={{ paddingTop: 4 }}>
            {scheduleClient && scheduleForm.reportSchedulerEnabled && (
              <Button
                variant="secondary"
                type="button"
                loading={sendingReport === scheduleClient.id}
                onClick={handleSendNowFromModal}
              >
                ▶ Send Now
              </Button>
            )}
            <Button variant="secondary" type="button" onClick={() => setScheduleModal(false)}>Cancel</Button>
            <Button loading={savingSchedule} onClick={handleSaveSchedule}>Save Schedule</Button>
          </div>
        </div>
      </Modal>

      {/* Assign Manager Modal */}
      <Modal isOpen={assignModal} onClose={() => setAssignModal(false)}
        title={`Assign Manager — ${selectedClient?.clientName}`} size="sm">
        <div className="space-y-4">
          <Select label="Recruiter Manager" value={selectedManager}
            onChange={e => setSelectedManager(e.target.value)}>
            <option value="">-- Select Manager --</option>
            {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.email})</option>)}
          </Select>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setAssignModal(false)}>Cancel</Button>
            <Button onClick={handleAssignManager} loading={assigning} loadingText="Assigning…">Assign</Button>
          </div>
        </div>
      </Modal>
            {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => setConfirmDelete(null)} />
          <div style={{ position: 'relative', background: isDark ? '#1e2535' : '#ffffff', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: isDark ? '#f1f5f9' : '#111827' }}>Delete Client?</h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: isDark ? '#94a3b8' : '#6b7280' }}>This will permanently remove the client and all associated data.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #d1d5db', background: 'transparent', color: isDark ? '#f1f5f9' : '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeleteConfirmed} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
